import { useEffect, useMemo, useState } from 'react';
import { getTrialBalance, listAccountingPeriods } from '../../api/finance';
import { usePermissions } from '../../context/PermissionContext';
import { EmptyState, ErrorNote, FieldLabel, LoadingNote, PageHeader, Select, tableStyle, tdStyle, thStyle } from '../../components/common';
import type { AccountingPeriod, TrialBalanceRow } from '../../types/domain';

const currency = (n: number) => `Rp ${n.toLocaleString('id-ID')}`;

/**
 * Financial Reports (FASE 10, migration 0044). Only reports that can be
 * deterministically produced from real posted journal data are shown.
 * Trial Balance is a direct read-time projection (fn_get_trial_balance).
 * P&L Draft and Balance Sheet are derived client-side by bucketing the
 * same trial balance rows by account_type/normal_balance — no separate
 * accounting policy is invented, this is arithmetic on real posted data.
 * Cash Flow requires classifying accounts into operating/investing/
 * financing activities, which this schema has no field for — shown as
 * BLOCKED_BY_SOURCE rather than fabricated. Bank/cash reconciliation has
 * no real bank-feed source anywhere in this schema — shown as BELUM
 * TERHUBUNG, never a fake balance.
 */
export default function FinancialReportsPage() {
  const { hasPermission } = usePermissions();
  const [tab, setTab] = useState<'trial_balance' | 'pnl' | 'balance_sheet' | 'cash_flow' | 'reconciliation'>('trial_balance');
  const [periods, setPeriods] = useState<AccountingPeriod[]>([]);
  const [periodId, setPeriodId] = useState('');
  const [rows, setRows] = useState<TrialBalanceRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canRead = hasPermission('journal_entry', 'read') && hasPermission('chart_of_account', 'read');

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    listAccountingPeriods()
      .then((rows) => {
        setPeriods(rows);
        if (rows.length > 0) setPeriodId(rows[0].period_id);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!periodId) {
      setRows(null);
      return;
    }
    setLoading(true);
    setError(null);
    getTrialBalance(periodId)
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [periodId]);

  const nonZeroRows = useMemo(() => (rows ?? []).filter((r) => r.total_debit !== 0 || r.total_credit !== 0), [rows]);
  const totalDebit = nonZeroRows.reduce((s, r) => s + r.total_debit, 0);
  const totalCredit = nonZeroRows.reduce((s, r) => s + r.total_credit, 0);

  const balanceOf = (r: TrialBalanceRow) => (r.normal_balance === 'DEBIT' ? r.total_debit - r.total_credit : r.total_credit - r.total_debit);
  const revenueRows = nonZeroRows.filter((r) => r.account_type === 'REVENUE');
  const expenseRows = nonZeroRows.filter((r) => r.account_type === 'EXPENSE');
  const totalRevenue = revenueRows.reduce((s, r) => s + balanceOf(r), 0);
  const totalExpense = expenseRows.reduce((s, r) => s + balanceOf(r), 0);
  const netIncome = totalRevenue - totalExpense;

  const assetRows = nonZeroRows.filter((r) => r.account_type === 'ASSET');
  const liabilityRows = nonZeroRows.filter((r) => r.account_type === 'LIABILITY');
  const equityRows = nonZeroRows.filter((r) => r.account_type === 'EQUITY');
  const totalAssets = assetRows.reduce((s, r) => s + balanceOf(r), 0);
  const totalLiabilities = liabilityRows.reduce((s, r) => s + balanceOf(r), 0);
  const totalEquity = equityRows.reduce((s, r) => s + balanceOf(r), 0) + netIncome;

  const tabs: Array<{ key: typeof tab; label: string }> = [
    { key: 'trial_balance', label: 'Trial Balance' },
    { key: 'pnl', label: 'Laba Rugi (Draft)' },
    { key: 'balance_sheet', label: 'Neraca (Draft)' },
    { key: 'cash_flow', label: 'Arus Kas' },
    { key: 'reconciliation', label: 'Rekonsiliasi Bank' },
  ];

  return (
    <div>
      <PageHeader title="Laporan Keuangan" subtitle="Hanya laporan yang bisa dihitung deterministik dari jurnal terposting nyata. Migrasi 0044." />
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--alex-border)', flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              border: 'none',
              background: 'none',
              padding: '10px 14px',
              fontSize: 'var(--alex-fs-caption)',
              fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? 'var(--alex-primary)' : 'var(--alex-text-secondary)',
              borderBottom: tab === t.key ? '2px solid var(--alex-primary)' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!canRead && <EmptyState text="Tidak ada izin untuk melihat laporan keuangan." />}
      {canRead && (tab === 'trial_balance' || tab === 'pnl' || tab === 'balance_sheet') && (
        <div style={{ width: 220, marginBottom: 16 }}>
          <FieldLabel label="Periode Akuntansi">
            <Select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
              {periods.length === 0 && <option value="">Belum ada periode</option>}
              {periods.map((p) => (
                <option key={p.period_id} value={p.period_id}>
                  {p.period_code} ({p.status === 'CLOSED' ? 'Ditutup' : 'Terbuka'})
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
      )}

      {canRead && loading && <LoadingNote />}
      {canRead && error && <ErrorNote text={error} />}

      {canRead && !loading && !error && tab === 'trial_balance' && (
        <>
          {periods.length === 0 && <EmptyState text="Belum ada periode akuntansi — buat periode di halaman Jurnal terlebih dahulu." />}
          {periods.length > 0 && nonZeroRows.length === 0 && <EmptyState text="Belum ada transaksi terposting pada periode ini." />}
          {nonZeroRows.length > 0 && (
            <>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Kode</th>
                    <th style={thStyle}>Akun</th>
                    <th style={thStyle}>Debit</th>
                    <th style={thStyle}>Kredit</th>
                  </tr>
                </thead>
                <tbody>
                  {nonZeroRows.map((r) => (
                    <tr key={r.account_id}>
                      <td style={tdStyle}>{r.account_code}</td>
                      <td style={tdStyle}>{r.name}</td>
                      <td style={tdStyle}>{r.total_debit > 0 ? currency(r.total_debit) : '—'}</td>
                      <td style={tdStyle}>{r.total_credit > 0 ? currency(r.total_credit) : '—'}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>
                      Total {totalDebit === totalCredit ? '(seimbang)' : '(TIDAK SEIMBANG)'}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalDebit)}</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalCredit)}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </>
      )}

      {canRead && !loading && !error && tab === 'pnl' && (
        <>
          {nonZeroRows.length === 0 && <EmptyState text="Belum ada transaksi terposting pada periode ini." />}
          {nonZeroRows.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Akun</th>
                  <th style={thStyle}>Jumlah</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>
                    Pendapatan
                  </td>
                </tr>
                {revenueRows.length === 0 && (
                  <tr>
                    <td style={tdStyle} colSpan={2}>
                      —
                    </td>
                  </tr>
                )}
                {revenueRows.map((r) => (
                  <tr key={r.account_id}>
                    <td style={tdStyle}>{r.name}</td>
                    <td style={tdStyle}>{currency(balanceOf(r))}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>
                    Beban
                  </td>
                </tr>
                {expenseRows.length === 0 && (
                  <tr>
                    <td style={tdStyle} colSpan={2}>
                      —
                    </td>
                  </tr>
                )}
                {expenseRows.map((r) => (
                  <tr key={r.account_id}>
                    <td style={tdStyle}>{r.name}</td>
                    <td style={tdStyle}>{currency(balanceOf(r))}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>Laba/Rugi Bersih (Draft)</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: netIncome >= 0 ? 'var(--alex-success)' : 'var(--alex-danger)' }}>{currency(netIncome)}</td>
                </tr>
              </tbody>
            </table>
          )}
          <p style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 10 }}>
            Draft — dihitung langsung dari jurnal terposting per periode ini, belum melalui proses tutup buku resmi (closing entries) lintas periode.
          </p>
        </>
      )}

      {canRead && !loading && !error && tab === 'balance_sheet' && (
        <>
          {nonZeroRows.length === 0 && <EmptyState text="Belum ada transaksi terposting pada periode ini." />}
          {nonZeroRows.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Akun</th>
                  <th style={thStyle}>Jumlah</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>
                    Aset
                  </td>
                </tr>
                {assetRows.map((r) => (
                  <tr key={r.account_id}>
                    <td style={tdStyle}>{r.name}</td>
                    <td style={tdStyle}>{currency(balanceOf(r))}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>Total Aset</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalAssets)}</td>
                </tr>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>
                    Liabilitas
                  </td>
                </tr>
                {liabilityRows.map((r) => (
                  <tr key={r.account_id}>
                    <td style={tdStyle}>{r.name}</td>
                    <td style={tdStyle}>{currency(balanceOf(r))}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>
                    Ekuitas
                  </td>
                </tr>
                {equityRows.map((r) => (
                  <tr key={r.account_id}>
                    <td style={tdStyle}>{r.name}</td>
                    <td style={tdStyle}>{currency(balanceOf(r))}</td>
                  </tr>
                ))}
                <tr>
                  <td style={tdStyle}>Laba/Rugi Berjalan (Draft)</td>
                  <td style={tdStyle}>{currency(netIncome)}</td>
                </tr>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>Total Liabilitas + Ekuitas</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalLiabilities + totalEquity)}</td>
                </tr>
              </tbody>
            </table>
          )}
          <p style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 10 }}>
            Draft — Total Aset seharusnya sama dengan Total Liabilitas + Ekuitas jika seluruh transaksi periode sebelumnya sudah tercermin di saldo awal (belum ada mekanisme saldo awal
            lintas periode di build ini).
          </p>
        </>
      )}

      {tab === 'cash_flow' && (
        <div className="alex-card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>BLOCKED_BY_SOURCE</div>
          <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)', lineHeight: 1.5, margin: 0 }}>
            Laporan Arus Kas membutuhkan klasifikasi setiap akun ke aktivitas operasi/investasi/pendanaan — skema saat ini tidak punya kolom itu di <code>chart_of_accounts</code>. Tidak
            dibuat di sini agar tidak mengarang kebijakan klasifikasi yang belum disetujui pemilik. <strong>NEEDS_DECISION.</strong>
          </p>
        </div>
      )}

      {tab === 'reconciliation' && (
        <div className="alex-card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>BELUM TERHUBUNG</div>
          <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)', lineHeight: 1.5, margin: 0 }}>
            Tidak ada sumber feed bank/kas nyata di skema saat ini (tidak ada tabel <code>bank_reconciliations</code> atau setara) — tidak ada saldo yang dikarang di sini. Rekonsiliasi
            manual baru bisa dibangun setelah ada model kanonik untuk transaksi bank/kas.
          </p>
        </div>
      )}
    </div>
  );
}
